import * as React from "react";
import SizedTextDataInput from "../../textDataInput/TextDataInput";
import { IDistributionPoolState } from "../../../../redux/types/IDistributionPoolState";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { hideModal } from "../../../../redux/reducers/ModalReducer";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { updateForeignSocietyInfo } from "../../../../redux/reducers/RepertoireReducer";
import { WideActionButton } from "../../actionButton/ActionButton";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import DropdownDataInput, { SizedDropdownDataInput } from "../../dropdownDataInput/DropdownDataInput";
import { getLookupValues } from "../../../../lookup/services/LookupHelpers";
import { CURRENCY_LOOKUP, DEDUCTION_CALCULATION_FORMULA_LOOKUP } from "../../../../lookup/Consts";
import { CURRENCY_STATE_KEY } from "../../../Consts";
import ModalFileUpload from "./ModalFileUpload";
import { IAttachedFile } from "../../../../redux/types/IAttachedFile";
import CheckboxDataInput from "../../checkboxDataInput/CheckboxDataInput";

export interface IForeignSocietyInfoViewProps {
    distributionPool: IDistributionPoolState;
    updateForeignSocietyInfo: (distributionPoolID: number, value: any, fieldName: string) => void;
    dataGridTableData: IRepertoireComponentDataItem;
    hideModal: typeof hideModal;
    lookupValues: ILookupDictionary;
    isReadOnly?: boolean;
    attachedFiles?: IAttachedFile[];
    onFileUploaded?: (file: IAttachedFile) => void;
    onFileRemoved?: (file: IAttachedFile) => void;
}

const isMissingMandatoryDeductionFormula = (applySocietySpecific: boolean, formulaValue: any) =>
    applySocietySpecific && (!formulaValue || formulaValue === "");

const ForeignSocietyInfoView: React.FunctionComponent<IForeignSocietyInfoViewProps> = ({ distributionPool, updateForeignSocietyInfo, dataGridTableData, hideModal, lookupValues, isReadOnly, attachedFiles, onFileUploaded, onFileRemoved }) => {
    const { distributionPoolID, witholdingTaxAgreement, witholdingTaxNoAgreement, socialAndCulturalDeduction, bankCharges, adminFees, deductionCalculationFormula, globalPoolDeductionRate, administrationRate, culturalDeductionsRate, applySocietySpecificCulturalDeductions, currency, exchangeRate, otherAdditions, otherDeductions, narrative } = distributionPool
    let fields: IRepertoireField[] = dataGridTableData.fields.filter(x => x.componentInstance === "ForeignSocietyInfo")

    const [validationMessage, setValidationMessage] = React.useState<string>("");
    const applySocietySpecificRef = React.useRef(applySocietySpecificCulturalDeductions.value);
    const deductionFormulaRef = React.useRef(deductionCalculationFormula.value);
    const updateForeignSocietyInfoRef = React.useRef(updateForeignSocietyInfo);

    React.useEffect(() => {
        applySocietySpecificRef.current = applySocietySpecificCulturalDeductions.value;
    }, [applySocietySpecificCulturalDeductions.value]);

    React.useEffect(() => {
        deductionFormulaRef.current = deductionCalculationFormula.value;
    }, [deductionCalculationFormula.value]);

    React.useEffect(() => {
        updateForeignSocietyInfoRef.current = updateForeignSocietyInfo;
    }, [updateForeignSocietyInfo]);

    React.useEffect(() => {
        return () => {
            if (isMissingMandatoryDeductionFormula(applySocietySpecificRef.current, deductionFormulaRef.current)) {
                updateForeignSocietyInfoRef.current(distributionPoolID, false, "applySocietySpecificCulturalDeductions");
            }
        };
    }, [distributionPoolID]);

    const validate = (distributionPoolID: number, value: any, fieldName: string) => {
        let val = isNaN(value) ? undefined : value;
        if (val === undefined) return false;
        updateForeignSocietyInfo(distributionPoolID, val, fieldName)
    }

    const toSignificantDigits = (distributionPoolID : number, value : any, fieldName: string, digits : number) =>{
        let val = isNaN(value) ? undefined : Math.abs(parseFloat(Number(value).toFixed(digits)));
        if (val === undefined) return false;
        updateForeignSocietyInfo(distributionPoolID, val, fieldName)
    }

    const handleOk = () => {
        if (isMissingMandatoryDeductionFormula(applySocietySpecificCulturalDeductions.value, deductionCalculationFormula.value)) {
            setValidationMessage(
                "Selection of a Deduction Calculation Formula option is mandatory for Society Specific Cultural Deductions"
            );
            return;
        }
        setValidationMessage("");
        hideModal();
    }
    
    return (
        <div className="col-md-12">
            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "witholdingTaxAgreement").data}
                    value={witholdingTaxAgreement.value === 0 ? "0.00" : witholdingTaxAgreement.value}
                    fieldName={""}
                    changeData={(x) => validate(distributionPoolID, x, "witholdingTaxAgreement")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "witholdingTaxAgreement", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />

                <SizedTextDataInput
                    label={fields.find(x => x.name === "witholdingTaxNoAgreement").data}
                    value={witholdingTaxNoAgreement.value === 0 ? "0.00" : witholdingTaxNoAgreement.value}
                    fieldName={""}
                    changeData={(x) => validate(distributionPoolID, x, "witholdingTaxNoAgreement")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "witholdingTaxNoAgreement", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "bankCharges").data}
                    value={bankCharges.value === 0 ? "0.00" : bankCharges.value}
                    fieldName={"subject"}
                    changeData={(x) => validate(distributionPoolID, x, "bankCharges")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "bankCharges", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}

                />
            </div>      
            
            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "adminFees").data}
                    value={adminFees.value === 0 ? "0.00" : adminFees.value}
                    fieldName={""}
                    changeData={(x) => validate(distributionPoolID, x, "adminFees")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "adminFees", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}

                />
            </div>
            
            <div className="row">
                <SizedDropdownDataInput
                    label={fields.find(x => x.name === "currency").data}
                    fieldName={CURRENCY_STATE_KEY}
                    value={currency.value}
                    changeData={(x) => updateForeignSocietyInfo(distributionPoolID, x, "currency")}
                    options={[...new Map(getLookupValues(CURRENCY_LOOKUP, lookupValues).map(item => [item.code, item])).values()]}
                    allowNull={true}
                    isHidden={false}
                    readOnly={isReadOnly}
                    />
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "exchangeRate").data}
                    value={exchangeRate.value === 1 ? "1.00" : exchangeRate.value}
                    fieldName={"subject"}
                    changeData={(x) => validate(distributionPoolID, x, "exchangeRate")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "exchangeRate", 10)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "socialAndCulturalDeduction").data}
                    value={socialAndCulturalDeduction.value === 0 ? "0.00" : socialAndCulturalDeduction.value}
                    fieldName={"subject"}
                    changeData={(x) => validate(distributionPoolID, x, "socialAndCulturalDeduction")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "socialAndCulturalDeduction", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />

                <SizedTextDataInput
                    label={fields.find(x => x.name === "otherAdditions").data}
                    value={otherAdditions.value === 0 ? "0.00" : otherAdditions.value}
                    fieldName={"subject"}
                    changeData={(x) => validate(distributionPoolID, x, "otherAdditions")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "otherAdditions", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />

                <SizedTextDataInput
                    label={fields.find(x => x.name === "otherDeductions").data}
                    value={otherDeductions.value === 0 ? "0.00" : otherDeductions.value}
                    fieldName={"subject"}
                    changeData={(x) => validate(distributionPoolID, x, "otherDeductions")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "otherAdditions", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />
            </div>

            <div className="row">
                <div className="col-md-6">
                    <DropdownDataInput
                        label={fields.find(x => x.name === "deductionCalculationFormula").data}
                        fieldName={"deductionCalculationFormula"}
                        value={deductionCalculationFormula.value}
                        changeData={(x) => updateForeignSocietyInfo(distributionPoolID, x, "deductionCalculationFormula")}
                        options={getLookupValues(DEDUCTION_CALCULATION_FORMULA_LOOKUP, lookupValues)}
                        allowNull={true}
                        isHidden={false}
                        readOnly={isReadOnly}
                    />
                </div>
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "globalPoolDeductionRate").data}
                    value={globalPoolDeductionRate.value === 0 ? "0.00" : globalPoolDeductionRate.value}
                    fieldName={""}
                    changeData={(x) => validate(distributionPoolID, x, "globalPoolDeductionRate")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "globalPoolDeductionRate", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "administrationRate").data}
                    value={administrationRate.value === 0 ? "0.00" : administrationRate.value}
                    fieldName={""}
                    changeData={(x) => validate(distributionPoolID, x, "administrationRate")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "administrationRate", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "culturalDeductionsRate").data}
                    value={culturalDeductionsRate.value === 0 ? "0.00" : culturalDeductionsRate.value}
                    fieldName={""}
                    changeData={(x) => validate(distributionPoolID, x, "culturalDeductionsRate")}
                    isOnBlurOn={true}
                    onBlurEvent={(x) => toSignificantDigits(distributionPoolID, x, "culturalDeductionsRate", 2)}
                    isHidden={false}
                    readOnly={isReadOnly}
                />
            </div>

            <div className="row" style={{ marginTop: "1rem" }}>
                <CheckboxDataInput
                    label={fields.find(x => x.name === "applySocietySpecificCulturalDeductions").data}
                    fieldName={"applySocietySpecificCulturalDeductions"}
                    value={applySocietySpecificCulturalDeductions.value}
                    changeData={(x) => updateForeignSocietyInfo(distributionPoolID, x, "applySocietySpecificCulturalDeductions")}
                    isHidden={false}
                    readonly={isReadOnly}
                />
            </div>

            <div className="row">
                <SizedTextDataInput
                    label={fields.find(x => x.name === "narrative").data}
                    value={narrative.value}
                    fieldName={"comment"}
                    changeData={(x) => updateForeignSocietyInfo(distributionPoolID, x, "narrative")}
                    textArea
                    isHidden={false}
                    readOnly={isReadOnly}

                />
            </div>

            <div className="row">
                <ModalFileUpload
                    entityId={distributionPoolID}
                    entityType="pool"
                    attachedFiles={attachedFiles}
                    onFileUploaded={onFileUploaded}
                    onFileRemoved={onFileRemoved}
                    isReadOnly={isReadOnly}
                />
            </div>

            {validationMessage && (
                <div className="row">
                    <span style={{ color: "red", fontWeight: 600 }}>{validationMessage}</span>
                </div>
            )}

            <div className="row">
                <WideActionButton
                    buttonText="OK"
                    buttonAction={handleOk}
                />
            </div>
        </div>
    );
};

export default ForeignSocietyInfoView;